import { IncomeExpenseChart } from "@/components/analytics/income-expense-chart";
import { CategoryPieChart } from "@/components/dashboard/category-pie-chart";
import { OverviewCards } from "@/components/dashboard/overview-cards";
import { useAppStore } from "@/lib/data";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart3, PieChart, TrendingUp } from "lucide-react";
import { useMemo } from "react";

export default function AnalyticsPage() {
    const { transactions, bills, businessRevenues, businessExpenses } = useAppStore();

    const allTransactions = useMemo(() => [
        ...transactions,
        ...bills.map(b => ({
            id: `bill-${b.id}`, date: b.dueDate, type: 'expense' as const,
            amount: Number(b.amount) || 0, description: b.name,
            category: 'Bills', category_id: '', workspace: b.workspace,
        })),
        ...businessRevenues.map(r => ({
            id: `rev-${r.id}`, date: r.date, type: 'income' as const,
            amount: Number(r.amount) || 0, description: r.description,
            category: r.category, category_id: '', workspace: 'business' as const,
        })),
        ...businessExpenses.map(e => ({
            id: `bexp-${e.id}`, date: e.date, type: 'expense' as const,
            amount: Number(e.amount) || 0, description: e.description,
            category: e.category, category_id: '', workspace: 'business' as const,
        })),
    ], [transactions, bills, businessRevenues, businessExpenses]);

    const hasData = allTransactions.length > 0;

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <TrendingUp className="h-6 w-6 text-primary" />
                        Analytics
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">A deeper look at your financial habits.</p>
                </div>

                {hasData ? (
                    <>
                        <OverviewCards transactions={allTransactions} />

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Income vs Expenses — wrapped in a single clean card */}
                            <Card className="border-0 shadow-md rounded-2xl overflow-hidden">
                                <div className="px-6 pt-6 pb-2">
                                    <p className="text-sm font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                        <BarChart3 className="h-4 w-4 text-primary" />
                                        Income vs Expenses
                                    </p>
                                </div>
                                <CardContent className="px-4 pb-4 pt-2">
                                    <IncomeExpenseChart transactions={allTransactions} />
                                </CardContent>
                            </Card>

                            {/* CategoryPieChart manages its own internal cards — no outer Card wrapper */}
                            <div className="space-y-0">
                                <CategoryPieChart transactions={allTransactions} />
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4">
                        {[
                            { Icon: TrendingUp, title: 'No Data Yet', desc: 'Add transactions to see income and expense trends.' },
                            { Icon: BarChart3, title: 'Track Progress', desc: 'Log income and expenses regularly to reveal patterns.' },
                            { Icon: PieChart, title: 'Category Breakdown', desc: 'See exactly where your money goes by category.' },
                        ].map(({ Icon, title, desc }) => (
                            <Card key={title} className="border-0 shadow-sm rounded-2xl">
                                <CardContent className="pt-8 pb-6 text-center space-y-3">
                                    <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center">
                                        <Icon className="h-7 w-7 text-slate-400" />
                                    </div>
                                    <h3 className="font-semibold text-slate-800">{title}</h3>
                                    <p className="text-sm text-muted-foreground">{desc}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
